import Alpine from 'alpinejs'
import persist from '@alpinejs/persist';
import mask from '@alpinejs/mask';
import collapse from '@alpinejs/collapse';
import Datepicker from 'flowbite-datepicker/Datepicker';
import DateRangePicker from 'flowbite-datepicker/DateRangePicker';
import imagesLoaded from 'imagesloaded';

window.Alpine = Alpine
window.Datepicker = Datepicker;
window.DateRangePicker = DateRangePicker;
window.imagesLoaded = imagesLoaded;

/**
 * FullCalendar and vis-timeline are used only in the owner-facing host account
 * and the calendar block, and together they are the bulk of the JS bundle.
 * Load them on demand so guest traffic never pays for them. The consumers
 * (full-calendar, host-account) await this before touching window.Calendar or
 * window.vis. Repeated calls resolve instantly once loaded.
 */
window.loadCalendarLibs = async () => {
    if (! window.Calendar) {
        const fc = await import('fullcalendar');
        window.Calendar = fc.Calendar;
    }
    if (! window.vis) {
        window.vis = await import('vis-timeline/standalone/index.js');
    }
};

Alpine.plugin(persist);
Alpine.plugin(mask);
Alpine.plugin(collapse);

window.$ = jQuery;

document.addEventListener('alpine:init', () => {
    Alpine.store('showModal', false);

    // Online Check-in Wizard Component
    Alpine.data('checkInWizard', (config) => ({
        // Step Management
        step: 1,
        totalSteps: 5,
        stepLabels: ['Terms & Conditions', 'Guest Information', 'Flight Details', 'Add-ons & Activities', 'Financial Breakdown'],

        // Loading States
        loading: true,
        submitting: false,
        error: null,

        // Mobile UI State
        mobileSummaryOpen: false,

        // Config from Blade
        reservationCode: config.reservationCode || '',
        pmsUrl: config.pmsUrl || '',
        stripePk: config.stripePk || '',
        returnUrl: config.returnUrl || '',

        // Client metadata
        clientUserAgent: typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '',
        clientIp: null,

        // Stripe (step 5) – two intents: charge (auto) + deposit (manual). Card Element + confirmCardPayment.
        stripe: null,
        elements: null,
        cardElement: null,
        chargeClientSecret: null,
        depositClientSecret: null,
        chargePaymentIntentId: null,
        depositPaymentIntentId: null,
        stripeElementsReady: false,
        paymentError: null, // Card/payment errors shown inline so user can fix and retry

        // Reservation Data
        reservation: null,

        // Step 1: Terms & Conditions
        termsAccepted: false,

        // Step 2: Guest Information
        accordionOpen: 1,
        mainGuest: {
            email: '',
            phone: '',
            marketingConsent: true,
            specialRequests: ''
        },
        passportInfo: {
            issuingCountry: '',
            passportNumber: '',
            homeAddress: '',
            phone: ''
        },
        allGuests: [],

        // Step 3: Flight Details + Airport Transfers
        flightDetails: {
            arrival: { flightNumber: '', landingTime: '' },
            departure: { flightNumber: '', landingTime: '' },
            noFlightDetails: false
        },
        airportTransfer: null,
        transferSpecialRequests: '',
        transferOptionsLoading: true,
        transferPriceAbortController: null,
        nightSurchargeAmount: 0,
        transferOptions: [
          { value: 'yes-1', label: 'Yes Please - Pick me up on arrival (1 Car, Max 4 Persons)', price: 25 },
          { value: 'yes-2', label: 'Yes please - Pick me up on arrival (2 Cars, Max 8 Persons)', price: 50 },
          { value: 'yes-departure', label: 'Yes please - Departure Only', price: 25 },
          { value: 'no', label: "No thanks - I'll make my own way", price: 0 }
        ],

        // Step 4: Add-ons
        availableAddons: [],
        addonsLoading: true,
        selectedAddonIds: [],
        addonModalOpen: false,
        selectedAddonForModal: null,

        // Tour Enquiries (ids for UI; full list for payload to PMS)
        enquiredTourIds: [],
        enquiredTours: [], // { id, title, message }[] sent to PMS
        enquiryModalOpen: false,
        selectedTourForEnquiry: null,
        enquiryMessage: '',
        enquirySubmitting: false,

        // Step 5: Payment
        cardholderName: '',

        // Eco Tax
        ecoRate: 1.5, // Default rate, will be fetched from API
        ecoMaxNights: 15, // Max chargeable eco nights per adult per stay (Malta cap), fetched from API; 0 = no cap

        // Countries list
        countries: [],

        // Computed values
        get guestCount() {
            return parseInt(this.reservation?.acf?.no_of_guests) || 0;
        },
        /** True when today is within online_checkin_window_days (inclusive) of check-in date. 0 = disabled. */
        get withinCheckinWindow() {
            const windowDays = this.reservation?.online_checkin_window_days ?? 7;
            if (windowDays <= 0) return false;
            const checkInStr = this.reservation?.acf?.['check-in_information']?.['check-in_date_and_time'];
            if (!checkInStr) return false;
            const checkIn = new Date(checkInStr);
            const today = new Date();
            // Date-only: strip time so we compare calendar days
            const checkInDate = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
            const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const msPerDay = 24 * 60 * 60 * 1000;
            const daysUntilCheckin = Math.round((checkInDate - todayDate) / msPerDay);
            // Within window: 0 <= daysUntilCheckin <= windowDays (e.g. from 7 days before up to and including check-in day)
            return daysUntilCheckin >= 0 && daysUntilCheckin <= windowDays;
        },
        get ecoTax() {
            if (!this.withinCheckinWindow) return 0;
            // Get values from reservation
            const noOfAdults = parseInt(this.reservation?.acf?.no_of_adults) || 0;
            const totalNights = parseFloat(this.reservation?.acf?.['check-in_information']?.total_nights) || 0;
            const storedEco = parseFloat(this.reservation?.acf?.['financial_&_payments']?.eco_contribution) || 0;

            // A stored eco contribution means it was already collected as part of the
            // booking payment (website checkout, or the OTA channel) - do NOT charge again.
            // Same semantics as the rental agreement, which marks stored eco as PAID.
            if (storedEco > 0) {
                return 0;
            }

            // Calculate: eco rate per adult per night, charged for at most ecoMaxNights nights (Malta cap)
            const ecoNights = this.ecoMaxNights > 0 ? Math.min(totalNights, this.ecoMaxNights) : totalNights;
            const perAdult = this.ecoRate * ecoNights;
            return Math.round(perAdult * noOfAdults * 100) / 100;
        },
        get ecoAlreadyPaid() {
            return (parseFloat(this.reservation?.acf?.['financial_&_payments']?.eco_contribution) || 0) > 0;
        },
        get securityDeposit() {
            if (!this.withinCheckinWindow) return 0;
            const raw = this.reservation?.property_details?.security_deposit;
            if (raw === null || raw === undefined || raw === '') return 200;
            const parsed = parseInt(String(raw).replace(/[^0-9]/g, ''));
            return isNaN(parsed) ? 200 : parsed;
        },
        get transferPrice() {
            const option = this.transferOptions.find(o => o.value === this.airportTransfer);
            return option ? option.price : 0;
        },
        get selectedAddons() {
            return this.availableAddons.filter(a => this.selectedAddonIds.includes(a.id));
        },
        get addonsByCategory() {
            return this.availableAddons.filter(a => a.category === 'addons');
        },
        get toursByCategory() {
            return this.availableAddons.filter(a => a.category === 'tours_offers');
        },
        get addonsTotal() {
            return this.selectedAddons.reduce((sum, a) => sum + a.price, 0);
        },
        get totalDueToday() {
            return this.ecoTax + this.addonsTotal;
        },
        /** Total amount to charge + authorize (eco + addons + security deposit when within window). */
        get paymentTotal() {
            return this.totalDueToday + this.securityDeposit;
        },
        get requiresPayment() {
            return this.paymentTotal > 0;
        },

        // Methods
        async init() {
            if (!this.reservationCode) {
                this.error = 'No reservation code provided. Please use the link from your booking confirmation email.';
                this.loading = false;
                return;
            }
            await Promise.all([
                this.fetchReservation(),
                this.fetchCountries(),
                this.fetchAddons(),
                this.fetchCityTaxSettings(),
                this.fetchClientIp()
            ]);

            // Watch for flight time changes to refetch transfer prices (for night surcharge)
            this.$watch('flightDetails.arrival.landingTime', () => {
                if (this.reservation) this.fetchAirportTransferPrices();
            });
            this.$watch('flightDetails.departure.landingTime', () => {
                if (this.reservation) this.fetchAirportTransferPrices();
            });
            // Refetch base prices when "no flight details" is toggled
            this.$watch('flightDetails.noFlightDetails', () => {
                if (this.reservation) this.fetchAirportTransferPrices();
            });
            // Init Stripe Elements when entering step 5 and payment required
            this.$watch('step', (step) => {
                if (step === 5 && this.requiresPayment && typeof window.Stripe !== 'undefined') {
                    this.$nextTick(() => this.initStripeElements());
                }
            });
        },

        buildCheckInPayload() {
            return {
                reservationCode: this.reservationCode,
                termsAcceptedAt: new Date().toISOString(),
                mainGuest: this.mainGuest,
                passportInfo: this.passportInfo,
                allGuests: this.allGuests,
                flightDetails: {
                    hasFlightDetails: !this.flightDetails.noFlightDetails,
                    arrival: this.flightDetails.arrival,
                    departure: this.flightDetails.departure
                },
                airportTransfer: {
                    type: this.airportTransfer,
                    price: this.transferPrice,
                    description: this.transferOptions.find(o => o.value === this.airportTransfer)?.label || '',
                    specialRequests: this.transferSpecialRequests || ''
                },
                selectedAddons: this.selectedAddons,
                enquiredTours: this.enquiredTours,
                totals: {
                    ecoTax: this.ecoTax,
                    addonsTotal: this.addonsTotal,
                    securityDeposit: this.securityDeposit,
                    totalCharged: this.totalDueToday
                },
                cardholderName: this.cardholderName || '',
                clientInfo: {
                    userAgent: this.clientUserAgent || '',
                    ip: this.clientIp || null
                }
            };
        },

        async fetchClientIp() {
            try {
                const response = await fetch('https://api.ipify.org?format=json');
                if (!response.ok) {
                    return;
                }
                const data = await response.json();
                if (data && data.ip) {
                    this.clientIp = data.ip;
                }
            } catch (e) {
                console.error('Error fetching client IP:', e);
            }
        },

        async initStripeElements() {
            if (!this.stripePk || !this.requiresPayment || this.elements) return;
            const amountCents = Math.round(this.paymentTotal * 100);
            if (amountCents <= 0) return;
            try {
                // Create PaymentIntent first (manual capture); Elements must use its clientSecret so capture_method matches
                const intentRes = await fetch('/wp-json/bv/v1/create-checkin-intent', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        amount_to_charge: this.totalDueToday,
                        amount_to_authorize: this.securityDeposit,
                        reservation_code: this.reservationCode,
                        eco_tax: this.ecoTax,
                        addons_total: this.addonsTotal,
                        payment_source: 'online_checkin'
                    })
                });
                const intentData = await intentRes.json();

                // If payment was already completed (idempotency returned existing succeeded PI), redirect to confirmation
                if (intentData.alreadyCompleted) {
                    const params = new URLSearchParams({ checkin_code: this.reservationCode, no_payment: '1' });
                    if (intentData.chargePaymentIntentId) params.set('payment_intent', intentData.chargePaymentIntentId);
                    if (intentData.depositPaymentIntentId) params.set('deposit_intent', intentData.depositPaymentIntentId);
                    window.location.replace(`${this.returnUrl}?${params.toString()}`);
                    return;
                }

                const firstSecret = intentData.chargeClientSecret || intentData.depositClientSecret;
                if (!firstSecret) {
                    throw new Error(intentData.message || 'Could not create payment session');
                }
                this.chargeClientSecret = intentData.chargeClientSecret || null;
                this.depositClientSecret = intentData.depositClientSecret || null;
                this.chargePaymentIntentId = intentData.chargePaymentIntentId || null;
                this.depositPaymentIntentId = intentData.depositPaymentIntentId || null;

                this.stripe = window.Stripe(this.stripePk);
                this.elements = this.stripe.elements({ clientSecret: firstSecret });
                this.cardElement = this.elements.create('card', {
                    style: {
                        base: {
                            fontSize: '16px',
                            color: '#0e4c92',
                            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif',
                            '::placeholder': {
                                color: '#94a3b8',
                            },
                            iconColor: '#0e7490',
                        },
                        invalid: {
                            color: '#dc2626',
                            iconColor: '#dc2626',
                        },
                    },
                });
                const container = document.getElementById('checkin-payment-element');
                if (container && container.children.length === 0) {
                    this.cardElement.mount('#checkin-payment-element');
                }
                this.stripeElementsReady = true;
            } catch (e) {
                console.error('Stripe init error:', e);
                this.error = e.message || 'Unable to load payment form. Please refresh the page.';
            }
        },

        async submitCheckIn() {
            this.submitting = true;
            const payload = this.buildCheckInPayload();

            if (!this.requiresPayment) {
                try {
                    await fetch('/wp-json/bv/v1/save-checkin-draft', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ reservationCode: this.reservationCode, payload })
                    });
                    const response = await fetch(`${this.pmsUrl}/wp-json/bvh/v1/check-in/submit`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });
                    const result = await response.json();
                    if (result.success && result.confirmationUrl) {
                        window.location.replace(result.confirmationUrl);
                    } else {
                        window.location.replace(`${this.returnUrl}?checkin_code=${encodeURIComponent(this.reservationCode)}&no_payment=1`);
                    }
                } catch (err) {
                    console.error(err);
                    this.error = 'Failed to complete check-in. Please try again.';
                }
                this.submitting = false;
                return;
            }

            try {
                this.paymentError = null;
                await fetch('/wp-json/bv/v1/save-checkin-draft', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ reservationCode: this.reservationCode, payload })
                });

                const baseReturn = `${this.returnUrl}?checkin_code=${encodeURIComponent(this.reservationCode)}`;
                const chargeReturnUrl = this.chargePaymentIntentId ? `${baseReturn}&payment_intent=${this.chargePaymentIntentId}` : baseReturn;

                if (this.chargeClientSecret) {
                    const { error, paymentIntent } = await this.stripe.confirmCardPayment(this.chargeClientSecret, {
                        payment_method: {
                            card: this.cardElement,
                            billing_details: { name: this.cardholderName || undefined }
                        },
                        return_url: chargeReturnUrl
                    });
                    if (error) {
                        this.paymentError = error.message || 'Payment failed. Please try again.';
                        this.submitting = false;
                        document.getElementById('checkin-payment-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        return;
                    }
                    if (paymentIntent && paymentIntent.status === 'requires_action') {
                        return;
                    }
                }

                const depositReturnUrl = `${baseReturn}${this.chargePaymentIntentId ? '&payment_intent=' + this.chargePaymentIntentId : ''}${this.depositPaymentIntentId ? '&deposit_intent=' + this.depositPaymentIntentId : ''}`;
                if (this.depositClientSecret) {
                    const { error, paymentIntent } = await this.stripe.confirmCardPayment(this.depositClientSecret, {
                        payment_method: {
                            card: this.cardElement,
                            billing_details: { name: this.cardholderName || undefined }
                        },
                        return_url: depositReturnUrl
                    });
                    if (error) {
                        this.paymentError = error.message || 'Deposit authorization failed. Please try again.';
                        this.submitting = false;
                        document.getElementById('checkin-payment-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        return;
                    }
                }

                // Redirect to confirmation (use replace to prevent back button returning here)
                const params = new URLSearchParams({ checkin_code: this.reservationCode });
                if (this.chargePaymentIntentId) params.set('payment_intent', this.chargePaymentIntentId);
                if (this.depositPaymentIntentId) params.set('deposit_intent', this.depositPaymentIntentId);
                window.location.replace(`${this.returnUrl}?${params.toString()}`);
            } catch (err) {
                console.error(err);
                this.paymentError = err.message || 'Payment failed. Please try again.';
                this.submitting = false;
                document.getElementById('checkin-payment-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },

        async fetchCountries() {
            try {
                const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2');
                if (!response.ok) throw new Error('Failed to fetch countries');
                const data = await response.json();
                this.countries = data
                    .map(c => ({ code: c.cca2, name: c.name.common }))
                    .sort((a, b) => a.name.localeCompare(b.name));
            } catch (error) {
                console.error('Error fetching countries:', error);
                // Fallback to common countries
                this.countries = [
                    { code: 'MT', name: 'Malta' },
                    { code: 'GB', name: 'United Kingdom' },
                    { code: 'DE', name: 'Germany' },
                    { code: 'FR', name: 'France' },
                    { code: 'IT', name: 'Italy' },
                    { code: 'ES', name: 'Spain' },
                    { code: 'US', name: 'United States' }
                ];
            }
        },

        async fetchCityTaxSettings() {
            try {
                const response = await fetch(`${this.pmsUrl}/wp-json/bvh/v1/city-tax-settings`);
                if (!response.ok) throw new Error('Failed to fetch city tax settings');
                const data = await response.json();

                if (data.success && data.city_tax_settings) {
                    const rate = data.city_tax_settings.eco_contribution_rate;
                    // Use rate if valid, otherwise default to 1.5
                    if (rate !== null && rate !== undefined && rate !== '') {
                        this.ecoRate = parseFloat(rate);
                    }
                    if ('eco_contribution_max_nights' in data.city_tax_settings) {
                        const maxNights = parseInt(data.city_tax_settings.eco_contribution_max_nights, 10);
                        this.ecoMaxNights = !isNaN(maxNights) && maxNights > 0 ? maxNights : 0;
                    }
                }
            } catch (error) {
                console.error('Error fetching city tax settings:', error);
                // Keep default rate of 1.5
            }
        },

        async fetchAddons() {
            this.addonsLoading = true;
            try {
                const response = await fetch(`${this.pmsUrl}/wp-json/bvh/v1/get-extras`);
                if (!response.ok) throw new Error('Failed to fetch addons');
                const data = await response.json();

                if (data.success && data.extras) {
                    // Helper to parse price string like "€65" or "€50 (in addition...)" or "from €495"
                    const parsePrice = (priceStr) => {
                        if (!priceStr) return 0;
                        const match = priceStr.match(/€(\d+)/);
                        return match ? parseInt(match[1]) : 0;
                    };

                    // Helper to extract subtitle from price (text in parentheses or after price)
                    const extractSubtitle = (priceStr) => {
                        if (!priceStr) return '';
                        // Check for parenthetical text like "(in addition to...)"
                        const parenMatch = priceStr.match(/\(([^)]+)\)/);
                        if (parenMatch) return parenMatch[1];
                        // Check for "from €X" format
                        if (priceStr.toLowerCase().startsWith('from ')) return 'starting price';
                        return '';
                    };

                    // Helper to get image URL from image object
                    const getImageUrl = (img) => {
                        if (!img) return '';
                        if (typeof img === 'string') return img;
                        return img.url || img.sizes?.medium || img.sizes?.thumbnail || '';
                    };

                    // Filter to only show addons marked for arrival form
                    this.availableAddons = data.extras
                        .filter(extra => extra.show_in_arrival_form === true)
                        .map((extra, index) => ({
                            id: extra.id || index + 1,
                            title: extra.title || '',
                            subtitle: extractSubtitle(extra.price),
                            price: parsePrice(extra.price),
                            image: getImageUrl(extra.image),
                            description: extra.description || '',
                            textboxTitle: extra.textbox_title || '',
                            category: extra.category || 'addons'
                        }));

                }
            } catch (error) {
                console.error('Error fetching addons:', error);
            } finally {
                this.addonsLoading = false;
            }
        },

        async fetchAirportTransferPrices() {
            // Cancel any pending request
            if (this.transferPriceAbortController) {
                this.transferPriceAbortController.abort();
            }
            this.transferPriceAbortController = new AbortController();
            const signal = this.transferPriceAbortController.signal;
            
            this.transferOptionsLoading = true;
            
            const propertyId = this.reservation?.acf?.property || this.reservation?.property_details?.post_id;
            if (!propertyId) {
                console.error('No property ID found for airport transfer pricing');
                this.transferOptionsLoading = false;
                return;
            }

            // Get times for surcharge calculation (skip if user has no flight details)
            const hasFlightDetails = !this.flightDetails.noFlightDetails;
            const arrivalTime = hasFlightDetails ? (this.flightDetails.arrival.landingTime || '') : '';
            const departureTime = hasFlightDetails ? (this.flightDetails.departure.landingTime || '') : '';

            try {
                // Build URL with optional time parameters
                let url = `${this.pmsUrl}/wp-json/bvh/v1/airport-transfer-prices?property_id=${propertyId}`;
                if (arrivalTime) url += `&arrival_time=${encodeURIComponent(arrivalTime)}`;
                if (departureTime) url += `&departure_time=${encodeURIComponent(departureTime)}`;

                const response = await fetch(url, { signal });
                const data = await response.json();

                if (data.success && data.prices) {
                    // Store night surcharge amount for disclaimer
                    this.nightSurchargeAmount = data.night_surcharge_amount || 0;

                    // Update transfer options with fetched prices
                    this.transferOptions = [
                        { value: 'yes-1', label: `Yes Please - Pick me up on arrival (${data.prices['yes-1']?.label || '1 Car, Max 4 Persons'})`, price: data.prices['yes-1']?.total_price || 0 },
                        { value: 'yes-2', label: `Yes Please - Pick me up on arrival (${data.prices['yes-2']?.label || '2 Cars, Max 8 Persons'})`, price: data.prices['yes-2']?.total_price || 0 },
                        { value: 'yes-departure', label: `Yes Please - ${data.prices['yes-departure']?.label || 'Departure Only'}`, price: data.prices['yes-departure']?.total_price || 0 },
                        { value: 'no', label: "No Thanks - I'll make my own way", price: 0 }
                    ];
                }

                this.transferOptionsLoading = false;
            } catch (error) {
                // Ignore abort errors (expected when cancelled)
                if (error.name === 'AbortError') return;
                console.error('Error fetching airport transfer prices:', error);
                this.transferOptionsLoading = false;
            }
        },

        async fetchReservation() {
            try {
                const response = await fetch(`${this.pmsUrl}/wp-json/bvh/v1/reservation/code/${this.reservationCode}`);
                if (!response.ok) {
                    throw new Error('Reservation not found');
                }
                const data = await response.json();
                this.reservation = data.reservation;


                // If online check-in was already completed in PMS, treat as done: send user to confirmation page
                const alreadyCheckedIn =
                    this.reservation?.online_checkin_completed === true ||
                    this.reservation?.acf?.online_checkin_completed === true ||
                    this.reservation?.acf?.online_check_in_completed === true ||
                    this.reservation?.acf?.['online_checkin_completed'] === true;
                if (alreadyCheckedIn) {
                    const params = new URLSearchParams({ checkin_code: this.reservationCode, no_payment: '1' });
                    window.location.href = `${this.returnUrl}?${params.toString()}`;
                    return;
                }

                // Pre-fill main guest info from ACF fields
                if (this.reservation.acf?.telephone) this.mainGuest.phone = this.reservation.acf.telephone;

                // Initialize all guests array based on guest count
                const guestCount = parseInt(this.reservation.acf?.no_of_guests) || 1;
                this.allGuests = Array.from({ length: guestCount }, (_, i) => ({
                    firstName: i === 0 ? (this.reservation.acf?.first_name || '') : '',
                    lastName: i === 0 ? (this.reservation.acf?.last_name || '') : ''
                }));

                this.loading = false;

                // Fetch airport transfer prices in background (don't block reservation loading)
                this.fetchAirportTransferPrices();
            } catch (error) {
                console.error('Error fetching reservation:', error);
                this.error = 'Unable to load your reservation. Please check your reservation code and try again.';
                this.loading = false;
            }
        },

        canProceed() {
            switch(this.step) {
                case 1:
                    return this.termsAccepted;
                case 2:
                    // Check main guest fields
                    if (!this.mainGuest.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.mainGuest.email) || !this.mainGuest.phone) return false;
                    // Check passport info
                    if (!this.passportInfo.issuingCountry || !this.passportInfo.passportNumber || !this.passportInfo.homeAddress) return false;
                    // Check all guests have names
                    return this.allGuests.every(g => g.firstName && g.lastName);
                case 3:
                    // Airport transfer selection required
                    return this.airportTransfer !== null;
                case 4:
                    return true; // Add-ons are optional
                case 5:
                    return true; // Pay button handles payment or complete
                default:
                    return true;
            }
        },

        nextStep() {
            if (this.canProceed() && this.step < this.totalSteps) {
                this.paymentError = null;
                this.step++;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },

        prevStep() {
            if (this.step > 1) {
                this.paymentError = null;
                this.step--;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },

        toggleAccordion(section) {
            this.accordionOpen = this.accordionOpen === section ? null : section;
        },

        toggleAddon(id) {
            if (this.selectedAddonIds.includes(id)) {
                this.selectedAddonIds = this.selectedAddonIds.filter(i => i !== id);
            } else {
                this.selectedAddonIds.push(id);
            }
        },

        openAddonModal(addon) {
            this.selectedAddonForModal = addon;
            this.addonModalOpen = true;
        },

        closeAddonModal() {
            this.addonModalOpen = false;
            // Delay clearing data until after transition completes (300ms)
            setTimeout(() => {
                this.selectedAddonForModal = null;
            }, 300);
        },

        openEnquiryModal(tour) {
            this.selectedTourForEnquiry = tour;
            this.enquiryMessage = '';
            this.enquiryModalOpen = true;
        },

        closeEnquiryModal() {
            this.enquiryModalOpen = false;
            setTimeout(() => {
                this.selectedTourForEnquiry = null;
                this.enquiryMessage = '';
            }, 300);
        },

        async submitEnquiry() {
            if (!this.selectedTourForEnquiry) return;

            this.enquirySubmitting = true;

            const tour = this.selectedTourForEnquiry;
            if (!this.enquiredTourIds.includes(tour.id)) {
                this.enquiredTourIds.push(tour.id);
                this.enquiredTours.push({
                    id: tour.id,
                    title: tour.title,
                    subtitle: tour.subtitle || '',
                    price: tour.price ?? 0,
                    description: this.enquiryMessage || ''
                });
            }

            this.enquirySubmitting = false;
            this.closeEnquiryModal();
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        },

    }));

    // Complete Payment Component (Eco Tax + Security Deposit only)
    Alpine.data('completePayment', (config) => ({
        // Loading States
        loading: true,
        submitting: false,
        error: null,

        // Config from Blade
        reservationCode: config.reservationCode || '',
        pmsUrl: config.pmsUrl || '',
        stripePk: config.stripePk || '',
        returnUrl: config.returnUrl || '',

        // Stripe
        stripe: null,
        elements: null,
        cardElement: null,
        chargeClientSecret: null,
        depositClientSecret: null,
        chargePaymentIntentId: null,
        depositPaymentIntentId: null,
        stripeElementsReady: false,
        paymentError: null,

        // Reservation Data
        reservation: null,
        depositAlreadyAuthorised: false,
        ecoTaxAlreadyCharged: false,

        // Payment
        cardholderName: '',

        // Eco Tax rate
        ecoRate: 1.5,
        ecoMaxNights: 15, // Max chargeable eco nights per adult per stay (Malta cap); 0 = no cap

        // Computed values
        get ecoTax() {
            const noOfAdults = parseInt(this.reservation?.acf?.no_of_adults) || 0;
            const totalNights = parseFloat(this.reservation?.acf?.['check-in_information']?.total_nights) || 0;
            const storedEco = parseFloat(this.reservation?.acf?.['financial_&_payments']?.eco_contribution) || 0;

            // A stored eco contribution means it was already collected as part of the
            // booking payment (website checkout, or the OTA channel) - do NOT charge again.
            if (storedEco > 0) {
                return 0;
            }

            // Eco rate per adult per night, charged for at most ecoMaxNights nights (Malta cap)
            const ecoNights = this.ecoMaxNights > 0 ? Math.min(totalNights, this.ecoMaxNights) : totalNights;
            const perAdult = this.ecoRate * ecoNights;
            return Math.round(perAdult * noOfAdults * 100) / 100;
        },
        get ecoAlreadyPaid() {
            return (parseFloat(this.reservation?.acf?.['financial_&_payments']?.eco_contribution) || 0) > 0;
        },
        get securityDeposit() {
            const raw = this.reservation?.property_details?.security_deposit;
            if (raw === null || raw === undefined || raw === '') return 200;
            const parsed = parseInt(String(raw).replace(/[^0-9]/g, ''));
            return isNaN(parsed) ? 200 : parsed;
        },
        get totalDueToday() {
            return this.ecoTax;
        },
        get paymentTotal() {
            return this.totalDueToday + this.securityDeposit;
        },

        // Methods
        async init() {
            if (!this.reservationCode) {
                this.error = 'No reservation code provided. Please use the link from your confirmation email.';
                this.loading = false;
                return;
            }
            await Promise.all([
                this.fetchReservation(),
                this.fetchCityTaxSettings()
            ]);

            if (this.depositAlreadyAuthorised && this.ecoTaxAlreadyCharged) return;

            this.loading = false;
            this.$nextTick(() => this.initStripeElements());
        },

        async fetchCityTaxSettings() {
            try {
                const response = await fetch(`${this.pmsUrl}/wp-json/bvh/v1/city-tax-settings`);
                if (!response.ok) throw new Error('Failed to fetch city tax settings');
                const data = await response.json();

                if (data.success && data.city_tax_settings) {
                    const rate = data.city_tax_settings.eco_contribution_rate;
                    if (rate !== null && rate !== undefined && rate !== '') {
                        this.ecoRate = parseFloat(rate);
                    }
                    if ('eco_contribution_max_nights' in data.city_tax_settings) {
                        const maxNights = parseInt(data.city_tax_settings.eco_contribution_max_nights, 10);
                        this.ecoMaxNights = !isNaN(maxNights) && maxNights > 0 ? maxNights : 0;
                    }
                }
            } catch (error) {
                console.error('Error fetching city tax settings:', error);
            }
        },

        async fetchReservation() {
            try {
                const response = await fetch(`${this.pmsUrl}/wp-json/bvh/v1/reservation/code/${this.reservationCode}`);
                if (!response.ok) {
                    throw new Error('Reservation not found');
                }
                const data = await response.json();
                this.reservation = data.reservation;
                this.depositAlreadyAuthorised = data.reservation.deposit_already_authorised === true;
                this.ecoTaxAlreadyCharged = data.reservation.eco_tax_already_charged === true;

                if (this.depositAlreadyAuthorised && this.ecoTaxAlreadyCharged) {
                    window.location.replace(`${this.returnUrl}?checkin_code=${encodeURIComponent(this.reservationCode)}&no_payment=1`);
                    return;
                }
            } catch (error) {
                console.error('Error fetching reservation:', error);
                this.error = 'Unable to load your reservation. Please check your reservation code and try again.';
                this.loading = false;
            }
        },

        async initStripeElements() {
            if (!this.stripePk || this.elements) return;
            const amountToCharge   = this.ecoTaxAlreadyCharged     ? 0 : this.totalDueToday;
            const amountToAuthorize = this.depositAlreadyAuthorised ? 0 : this.securityDeposit;
            if (Math.round(amountToCharge * 100) <= 0 && Math.round(amountToAuthorize * 100) <= 0) return;
            try {
                const intentRes = await fetch('/wp-json/bv/v1/create-checkin-intent', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        amount_to_charge: amountToCharge,
                        amount_to_authorize: amountToAuthorize,
                        reservation_code: this.reservationCode,
                        eco_tax: this.ecoTaxAlreadyCharged ? 0 : this.ecoTax,
                        addons_total: 0,
                        payment_source: 'complete_payment'
                    })
                });
                const intentData = await intentRes.json();

                // If payment was already completed (idempotency returned existing succeeded PI), redirect to confirmation
                if (intentData.alreadyCompleted) {
                    const params = new URLSearchParams({ checkin_code: this.reservationCode, no_payment: '1' });
                    if (intentData.chargePaymentIntentId) params.set('payment_intent', intentData.chargePaymentIntentId);
                    if (intentData.depositPaymentIntentId) params.set('deposit_intent', intentData.depositPaymentIntentId);
                    window.location.replace(`${this.returnUrl}?${params.toString()}`);
                    return;
                }

                const firstSecret = intentData.chargeClientSecret || intentData.depositClientSecret;
                if (!firstSecret) {
                    throw new Error(intentData.message || 'Could not create payment session');
                }
                this.chargeClientSecret = intentData.chargeClientSecret || null;
                this.depositClientSecret = intentData.depositClientSecret || null;
                this.chargePaymentIntentId = intentData.chargePaymentIntentId || null;
                this.depositPaymentIntentId = intentData.depositPaymentIntentId || null;

                this.stripe = window.Stripe(this.stripePk);
                this.elements = this.stripe.elements({ clientSecret: firstSecret });
                this.cardElement = this.elements.create('card', {
                    style: {
                        base: {
                            fontSize: '16px',
                            color: '#0e4c92',
                            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            '::placeholder': {
                                color: '#94a3b8',
                            },
                            iconColor: '#0e7490',
                        },
                        invalid: {
                            color: '#dc2626',
                            iconColor: '#dc2626',
                        },
                    },
                });
                const container = document.getElementById('complete-payment-element');
                if (container && container.children.length === 0) {
                    this.cardElement.mount('#complete-payment-element');
                }
                this.stripeElementsReady = true;
            } catch (e) {
                console.error('Stripe init error:', e);
                this.error = e.message || 'Unable to load payment form. Please refresh the page.';
            }
        },

        buildPaymentPayload() {
            return {
                reservationCode: this.reservationCode,
                paymentType: 'complete_payment',
                totals: {
                    ecoTax: this.ecoTax,
                    securityDeposit: this.securityDeposit,
                    totalCharged: this.totalDueToday
                },
                cardholderName: this.cardholderName || ''
            };
        },

        async submitPayment() {
            this.submitting = true;
            const payload = this.buildPaymentPayload();

            try {
                this.paymentError = null;

                // Save draft before payment
                await fetch('/wp-json/bv/v1/save-checkin-draft', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ reservationCode: this.reservationCode, payload })
                });

                const baseReturn = `${this.returnUrl}?checkin_code=${encodeURIComponent(this.reservationCode)}`;
                const chargeReturnUrl = this.chargePaymentIntentId ? `${baseReturn}&payment_intent=${this.chargePaymentIntentId}` : baseReturn;

                // Process charge (eco tax)
                if (this.chargeClientSecret) {
                    const { error, paymentIntent } = await this.stripe.confirmCardPayment(this.chargeClientSecret, {
                        payment_method: {
                            card: this.cardElement,
                            billing_details: { name: this.cardholderName || undefined }
                        },
                        return_url: chargeReturnUrl
                    });
                    if (error) {
                        this.paymentError = error.message || 'Payment failed. Please try again.';
                        this.submitting = false;
                        document.getElementById('complete-payment-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        return;
                    }
                    if (paymentIntent && paymentIntent.status === 'requires_action') {
                        return;
                    }
                }

                // Process deposit authorization
                const depositReturnUrl = `${baseReturn}${this.chargePaymentIntentId ? '&payment_intent=' + this.chargePaymentIntentId : ''}${this.depositPaymentIntentId ? '&deposit_intent=' + this.depositPaymentIntentId : ''}`;
                if (this.depositClientSecret) {
                    const { error, paymentIntent } = await this.stripe.confirmCardPayment(this.depositClientSecret, {
                        payment_method: {
                            card: this.cardElement,
                            billing_details: { name: this.cardholderName || undefined }
                        },
                        return_url: depositReturnUrl
                    });
                    if (error) {
                        this.paymentError = error.message || 'Deposit authorization failed. Please try again.';
                        this.submitting = false;
                        document.getElementById('complete-payment-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        return;
                    }
                }

                // Redirect to confirmation (use replace to prevent back button returning here)
                const params = new URLSearchParams({ checkin_code: this.reservationCode });
                if (this.chargePaymentIntentId) params.set('payment_intent', this.chargePaymentIntentId);
                if (this.depositPaymentIntentId) params.set('deposit_intent', this.depositPaymentIntentId);
                window.location.replace(`${this.returnUrl}?${params.toString()}`);
            } catch (err) {
                console.error(err);
                this.paymentError = err.message || 'Payment failed. Please try again.';
                this.submitting = false;
                document.getElementById('complete-payment-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }));

    // Every $persist() below is aliased with .as().
    //
    // Alpine keys $persist on the property name alone (`_x_${path}`, and stores are
    // passed to initInterceptors as their own root), NOT on the store name. So an
    // un-aliased `booking.adults` and `search.adults` both read and write the single
    // localStorage key `_x_adults`, and the search bar's string values silently
    // overwrite the booking's numeric ones. That is what sent `no_of_guests: "40"`
    // ("4" + 0) to Rentals United on 27-08-2026 and cost us a paid reservation.
    // `dateFrom`/`dateTo` collided the same way, which is worse: it produces a
    // perfectly valid booking for the wrong nights. Keep the aliases unique.
    Alpine.store('booking', {
        dateFrom: Alpine.$persist('').as('booking_dateFrom'),
        dateTo: Alpine.$persist('').as('booking_dateTo'),
        property: Alpine.$persist('').as('booking_property'),
        adults: Alpine.$persist(2).as('booking_adults'),
        children: Alpine.$persist(0).as('booking_children'),
        price: Alpine.$persist('').as('booking_price'),
        originalPrice: Alpine.$persist('').as('booking_originalPrice'),
        discountPercentage: Alpine.$persist(0).as('booking_discountPercentage'),
        total: Alpine.$persist('').as('booking_total'),
        totalRentReceived: Alpine.$persist('').as('booking_totalRentReceived'),
        channelCommission: Alpine.$persist('').as('booking_channelCommission'),
        deposit: Alpine.$persist('').as('booking_deposit'),
        ecoTax: Alpine.$persist('').as('booking_ecoTax'),
        flightNo: Alpine.$persist('').as('booking_flightNo'),
        landingTime: Alpine.$persist('').as('booking_landingTime'),
        extras: Alpine.$persist('').as('booking_extras'),
        comments: Alpine.$persist('').as('booking_comments'),
        email: Alpine.$persist('').as('booking_email'),
        firstName: Alpine.$persist('').as('booking_firstName'),
        lastName: Alpine.$persist('').as('booking_lastName'),
        phone: Alpine.$persist('').as('booking_phone'),
    });
    // adults/children default to real numbers, not ''. They used to be declared as
    // '' and still showed "2 adults, 0 children" on a first visit, but only by
    // accident: this store shared the _x_adults and _x_children keys with `booking`
    // above, whose defaults are 2 and 0, and `booking` is registered first so it
    // wrote them before this store read them. Separating the keys removed that
    // accident and left the guest picker blank, and the search button inert, since
    // it gates on `$store.search.adults` being truthy.
    Alpine.store('search', {
        dateFrom: Alpine.$persist('').as('search_dateFrom'),
        dateTo: Alpine.$persist('').as('search_dateTo'),
        adults: Alpine.$persist(2).as('search_adults'),
        children: Alpine.$persist(0).as('search_children'),
        locationId: Alpine.$persist('').as('search_locationId'),
        location: Alpine.$persist('').as('search_location'),
    })
    Alpine.store('reservations', {
        upcomingReservations: Alpine.$persist('').as('reservations_upcoming'),
        pastReservations: Alpine.$persist('').as('reservations_past'),
    })
    // Alpine.store('wishlist', {
    //    properties: Alpine.$persist(''),

    //    addToWishlist(property) {
    //     this.properties = this.properties + ' ' + property;
    //     console.log(this.properties);
    //    },

    //    removeFromWishlist(property) {
    //     var modifiedString = this.properties.replace(property, "");
    //     this.properties = modifiedString;
    //     console.log(this.properties);
    //     },

    //     updateWishlist() {
    //         var wishlistArray = this.properties.split(' ');
    //         var newWishlist = this.removeDuplicates(wishlistArray);
    //         var updatedWishlist = [];
    //         $.each(newWishlist, function(index, value) {
    //             if(value != '') {
    //                 const response = fetch('https://bvh-pms.lbh.studio/wp-json/bvh/v1/property-by-ru-id/' + value)
    //                 .then(function(response) {
    //                     return response.json();
    //                 })
    //                 .then(function(data) {
    //                     updatedWishlist.push(data);
    //                 })
    //             }
    //         })
    //         return updatedWishlist;
    //     },

    //     removeDuplicates(arr) {
    //         const uniqueSet = new Set(arr);
    //         const uniqueArray = Array.from(uniqueSet);
        
    //         return uniqueArray;
    //     }


    // })
    Alpine.store('wishlist', {
        // Deliberately NOT aliased: `wishlist` collides with nothing, and renaming the
        // key would orphan every visitor's saved wishlist in localStorage.
        wishlist: Alpine.$persist([]),
        addToWishlist(property) {
            this.inWishlist = !this.inWishlist;
    
            let wishlist = this.wishlist;
            if(!wishlist) {
                wishlist = [];
            }
    
            if(wishlist.includes(property.id)){
                wishlist = wishlist.filter(item => item !== property.id);
            }else{
                wishlist.push(property.id);
            }
            this.wishlist = wishlist;
    
            fetch('/wp-json/bv/v1/set-wishlist', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': window.sage.nonce,
                },
                body: JSON.stringify({
                    wishlist: wishlist,
                }),
            })
            .then(function(response) {
                // Check if the response status is OK (200)
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                // Return the JSON promise
                return response.json();
            })
            .then(function(data) {
    
            })
            .catch(function(error) {
                // Handle any errors here
                console.log('error', error);
            });
        }
    });

    Alpine.store('getNestedProperty', function(obj, path) {
        const keys = path.split('.');
        let value = obj;
        for (const key of keys) {
            value = value[key];
            if (value === undefined) {
            break;
            }
        }
        return value;
      });

      Alpine.store('formatDate', function(dateString) {
        var parts = dateString.split(' ')[0].split('-');
        var year = parseInt(parts[0]);
        var month = parseInt(parts[1]) - 1; // Months are 0-based in JavaScript
        var day = parseInt(parts[2]);
        
        var inputDate = new Date(Date.UTC(year, month, day, 0, 0, 0));
        var newDate = inputDate.toISOString().split('T')[0].split('-').reverse().join('-');
        return newDate;
      });

    Alpine.store('getSrcset', function(imageSizes) {
        let srcset = '';
        for (const [key, value] of Object.entries(imageSizes)) {
            if (key.endsWith('-width')) continue; // Skip width keys
            if (key.endsWith('-height')) continue; // Skip height keys

            const width = imageSizes[key + '-width'];
            if (width) {
                srcset += `${value} ${width}w, `;
            }
        }
        return srcset.slice(0, -2); // Remove the trailing comma and space
    });

    Alpine.store('exportCSV', function(items, columns, title = null){
        var exportData = ''; // Initialize as an empty string
    
        // Add title if provided
        if (title) {
          exportData += '"' + title.replace(/"/g, '""') + '"\r\n';
        }
    
        columns.forEach((column, index,) => {
          if(typeof column.show == 'undefined' || column.show) {
            exportData += '"' + column.name.replace(/"/g, '""') + '"';
            if(index < columns.length - 1){
              exportData += ',';
            }
          }
        })
        exportData += '\r\n';
    
        items.forEach(element => {
          columns.forEach((column, index) => {
            if(typeof column.show == 'undefined' || column.show) {
              var value = Alpine.store('getNestedProperty')(element, column.id);
    
              if(value && typeof value === 'object' && ('rendered' in value)){
                value = value.rendered;
              }
    
              if(value && typeof value === 'object' && ('post_name' in value)){
                value = value.post_name;
              }
    
              if(value && typeof value === 'object') {
                value = JSON.stringify(value);
              }
    
              if(value){
                value = value.toString().replace(/€/g, '');
    
                // Wrap the value in double quotes if it contains a comma
                if (value.includes(',')) {
                  value = value.replace(/,/g, ' '); // Replace with an empty string
                }
    
                // Check if the column is a telephone number and prepend a single quote
                if (column.isTelephone) {
                  value = "'" + value;
                }
              }
    
              if(!containsHTML(value)){
                exportData += '"' + value + '"';
              }else{
                exportData += '"yes"';
              }
              if(index < columns.length - 1){
                exportData += ',';
              }
            }
          })
          exportData += '\r\n';
        });
    
        let filename = 'export_'+Date.now()+'.csv';
        let element = document.createElement('a');
        element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(exportData));
        element.setAttribute('download', filename);
        element.click();
    });

    Alpine.store('base64toBlob', function(base64Data, contentType){
        const sliceSize = 512;
        const byteCharacters = atob(base64Data);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        return new Blob(byteArrays, { type: contentType });
    });

    function containsHTML(str) {
        const htmlRegex = /<("[^"]*"|'[^']*'|[^'">])*>/;
        return htmlRegex.test(str);
    }
})
 
Alpine.start();
